// avoid  editing this pain
import React, { useEffect, useRef } from 'react';

interface Grain {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  size: number;
  opacity: number;
  targetOpacity: number;
  speed: number;
}

const GrainBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0, y: 0, prevX: 0, prevY: 0, isMoving: false });
  const grainsRef = useRef<Grain[]>([]);
  const animationFrameRef = useRef<number>();
  const lastMoveTimeRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initialize grains - like beach sand
    const initGrains = () => {
      grainsRef.current = [];
      const width = canvas.width;
      const height = canvas.height;

      // Create grains in a grid pattern for even distribution, then randomize positions
      const spacing = 12; // Even tighter for more density
      const cols = Math.floor(width / spacing);
      const rows = Math.floor(height / spacing);

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          // Base position on grid
          const baseX = col * spacing;
          const baseY = row * spacing;

          // Add randomness to position
          const x = baseX + (Math.random() - 0.5) * spacing * 2;
          const y = baseY + (Math.random() - 0.5) * spacing * 2;

          grainsRef.current.push({
            x,
            y,
            targetX: x,
            targetY: y,
            size: Math.random() * 0.8 + 0.5, // Smaller grains (0.5-1.3px)
            opacity: 0, // Start invisible
            targetOpacity: Math.random() * 0.12 + 0.03, // More subtle (0.03-0.15)
            speed: Math.random() * 0.015 + 0.008, // Bit faster (0.008-0.023)
          });
        }
      }

    };

    // Set canvas size to window size
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      // Reinitialize grains on resize
      initGrains();
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    // Track mouse position and movement
    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      const prevX = mouseRef.current.x;
      const prevY = mouseRef.current.y;

      mouseRef.current.prevX = prevX;
      mouseRef.current.prevY = prevY;
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
      mouseRef.current.isMoving = true;
      lastMoveTimeRef.current = now;
    };
    window.addEventListener('mousemove', handleMouseMove);

    // Generate new grains at random positions (far from mouse)
    const generateNewGrains = (count: number) => {
      for (let i = 0; i < count; i++) {
        // Generate at edges or random far positions
        let x, y;
        const edge = Math.floor(Math.random() * 4); // 0=top, 1=right, 2=bottom, 3=left

        if (edge === 0) {
          // Top edge
          x = Math.random() * canvas.width;
          y = Math.random() * 100;
        } else if (edge === 1) {
          // Right edge
          x = canvas.width - Math.random() * 100;
          y = Math.random() * canvas.height;
        } else if (edge === 2) {
          // Bottom edge
          x = Math.random() * canvas.width;
          y = canvas.height - Math.random() * 100;
        } else {
          // Left edge
          x = Math.random() * 100;
          y = Math.random() * canvas.height;
        }

        grainsRef.current.push({
          x,
          y,
          targetX: x,
          targetY: y,
          size: Math.random() * 0.8 + 0.5, // Smaller grains (0.5-1.3px)
          opacity: 0,
          targetOpacity: Math.random() * 0.12 + 0.03, // More subtle (0.03-0.15)
          speed: Math.random() * 0.015 + 0.008, // Bit faster (0.008-0.023)
        });
      }
    };

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Check if mouse has stopped moving (no movement for 100ms)
      const now = Date.now();
      const timeSinceLastMove = now - lastMoveTimeRef.current;
      if (timeSinceLastMove > 100) {
        mouseRef.current.isMoving = false;
      }

      let grainsDestroyed = 0;

      grainsRef.current = grainsRef.current.filter((grain) => {
        // Fade in when mouse moves
        if (mouseRef.current.isMoving) {
          if (grain.opacity < grain.targetOpacity) {
            grain.opacity += 0.01; // Much faster fade in
          }
        }

        // Calculate distance and direction to mouse
        const dx = mouseRef.current.x - grain.x;
        const dy = mouseRef.current.y - grain.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Always apply sinkhole effect as long as grains are visible
        if (grain.opacity > 0.01 && distance > 5) {
          // Normalize direction
          const dirX = dx / distance;
          const dirY = dy / distance;

          // Circular sinkhole effect with strong pull radius
          const sinkholeRadius = 500; // Large radius for dramatic effect
          const destructionRadius = 12; // Small tight center for destruction

          if (distance < sinkholeRadius) {
            // Calculate pull strength - exponential increase as grains get closer
            const normalizedDist = distance / sinkholeRadius;
            const pullStrength = Math.pow(1 - normalizedDist, 2); // Exponential pull

            // Destroy grains when they reach the center (larger radius)
            if (distance <= destructionRadius) {
              grainsDestroyed++;
              return false; // Remove this grain
            }

            // Direct pull toward center
            const pullForce = pullStrength * grain.speed * 30;
            grain.x += dirX * pullForce;
            grain.y += dirY * pullForce;
          } else {
            // Gentle pull from far away
            grain.x += dirX * grain.speed * 2;
            grain.y += dirY * grain.speed * 2;
          }
        }

        // Minimal random drift
        grain.x += (Math.random() - 0.5) * 0.02;
        grain.y += (Math.random() - 0.5) * 0.02;

        // Keep grains within bounds with wrapping
        if (grain.x < -10) grain.x = canvas.width + 10;
        if (grain.x > canvas.width + 10) grain.x = -10;
        if (grain.y < -10) grain.y = canvas.height + 10;
        if (grain.y > canvas.height + 10) grain.y = -10;

        // Only draw if visible
        if (grain.opacity > 0.01) {
          // Calculate distance to mouse for intensity gradient
          const distToMouse = Math.sqrt(
            Math.pow(mouseRef.current.x - grain.x, 2) +
            Math.pow(mouseRef.current.y - grain.y, 2)
          );

          // Intensity radius - particles are brighter within 300px of mouse
          const intensityRadius = 300;
          let finalOpacity = grain.opacity;

          if (distToMouse < intensityRadius) {
            // Boost opacity for grains near mouse (up to 5x brighter)
            const proximityBoost = 1 + (4 * (1 - distToMouse / intensityRadius));
            finalOpacity = Math.min(grain.opacity * proximityBoost, 0.6);
          }

          // Draw grain with brand red color (#DC2626)
          ctx.fillStyle = `rgba(220, 38, 38, ${finalOpacity})`;
          ctx.beginPath();
          ctx.arc(grain.x, grain.y, grain.size, 0, Math.PI * 2);
          ctx.fill();
        }

        return true; // Keep this grain
      });

      // Generate new grains to replace destroyed ones
      if (grainsDestroyed > 0) {
        generateNewGrains(grainsDestroyed);
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup - prevent memory leaks
    return () => {
      window.removeEventListener('resize', setCanvasSize);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Clear all grains to prevent memory leaks
      grainsRef.current = [];
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{
        zIndex: 25,
        opacity: 0.7,
      }}
    />
  );
};

export default GrainBackground;
